/**
 * Flight/train journey contract used to describe a way and its segments.
 *
 * Mirrors `@lmn/lmn-components/components/Trip/JourneyContract`, which the Trip components consume,
 * but is NOT a subset of it - the two have drifted in both directions:
 * - only there: `JourneyContractSegmentTransportType.BUS`, `JourneyContractBaggage.smallBag`,
 *   `JourneyContractWay.selectedSeats`, `JourneyContractSegment.duration`,
 *   `JourneyContractSegmentTransport.PNR`, and an optional `durationInMinutes`
 * - only here: `JourneyContractBaggage.priceFrom` (unused - the app reads
 *   `TransportData.baggagePriceFrom` instead), and a required `durationInMinutes`
 *
 * Values only ever flow from here into the Trip components, which is the assignable direction.
 * Only the parts the app builds itself are kept here.
 *
 * So: data the app builds - anything reached through Product or TransportData - uses these types and
 * enums, and only props handed straight to an @lmn/lmn-components component use that package's.
 */

type TripCardContractPrice = {
  amount: number;
  tooltip?: string;
};

export type JourneyContractLocations = {
  origin: string;
  destination: string;
};

export type JourneyContractWay = {
  segments: Array<JourneyContractSegment>;
  /** Optional info about price and taxes. [ OBE: false; CNCR: true except promo; ] */
  priceInformation?: JourneyContractWayPrice;
  seats?: number;
  transportCategory: JourneyContractWayTransportCategory;
  durationInMinutes: number;
  selected: boolean;
  onSelected?: (checked: boolean) => void;
  baggage: JourneyContractBaggage;
  locations: JourneyContractLocations;
  /** Used only on details when search date different from trip date. Summaries components now rely on JourneySummaryContract.DateAsText */
  differentDepartureDate?: boolean;
};

export type JourneyContractBaggage = {
  hand?: boolean;
  hold?: boolean;
  priceFrom?: number;
} | null;

export interface JourneyContractWayPrice {
  price: {
    perPassenger: TripCardContractPrice;
  };
  taxes?: {
    perPassenger: TripCardContractPrice;
  };
}

export type JourneyContractSegment = {
  /** A stop usually to refuel or change plane.
   * on the UI it adds a NEW segment with the tech stop in the middle
   */
  technicalStops?: Array<JourneyContractTechnicalStop>;
  transport: JourneyContractSegmentTransport;
  departure: JourneyContractSegmentLocation;
  arrival: JourneyContractSegmentLocation;
};

export interface JourneyContractTechnicalStop {
  arrivalTime: string;
  departureTime: string;
  hub: JourneyContractHub;
}

export type JourneyContractSegmentLocation = {
  hub: JourneyContractHub;
  datetime: string;
};

export type JourneyContractSegmentTransport = {
  class: string;
  /** ID of transport, eg: UX1234 */
  operativeId: string;
  type: JourneyContractSegmentTransportType;
  /** needed for: "Operated by Air France" */
  operateBy?: {
    /** if it matches companyCode, all the "operated by" node won't be displayed */
    code: string;
    name: string;
  };
  companyCode: string;
  companyName: string;
};

export type JourneyContractHub = {
  id: string;
  name: string;
  terminal?: string;
};

export enum JourneyContractSegmentTransportType {
  FLIGHT = 'FLIGHT',
  TRAIN = 'TRAIN',
}

export enum JourneyContractWayTransportCategory {
  CHARTER = 'CHARTER',
  STANDARD = 'STANDARD',
}
